import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { ColorTrait } from "@/data/colors";
import { ShareButtons } from "@/components/social/ShareButtons";
import { buildInviteUrl } from "@/lib/compatibility";
import { Download, Users } from "lucide-react";

interface IdentityCardProps {
  title: string;
  colors: ColorTrait[]; // dominant colors, used for the palette strip
  traits: string[]; // 3 bulleted traits
}

/**
 * The shareable "Color Identity Card" shown at the end of a quiz — a
 * self-contained, exportable visual (not just a text link) designed to work
 * as an actual social share image, plus a compatibility invite link that
 * encodes this result into the URL so a friend's result can be compared
 * against it with no backend or accounts involved.
 */
export function IdentityCard({ title, colors, traits }: IdentityCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(false);

  const inviteUrl = buildInviteUrl({ title, colorIds: colors.map((c) => c.id) });

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // skipFonts avoids html-to-image trying to inline the remote Google
      // Fonts stylesheet, which throws a SecurityError in most browsers
      // (can't read cssRules from a cross-origin sheet without CORS).
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "my-color-identity.png";
      link.click();
    } catch {
      // Canvas export can fail in some locked-down browser contexts — the
      // card is still fully visible and shareable via the link buttons below.
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedInvite(true);
      setTimeout(() => setCopiedInvite(false), 2000);
    } catch {
      // Clipboard unavailable — the invite link is also included in the
      // native/social share text below.
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div
        ref={cardRef}
        className="w-full max-w-sm rounded-3xl overflow-hidden border border-black/5 shadow-lg"
        style={{ background: `linear-gradient(150deg, ${colors[0]?.hex ?? "#ccc"}22, transparent 60%)` }}
      >
        <div className="bg-card px-6 py-8 sm:px-8 sm:py-10">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-4">
            Mychromamind · Color Identity
          </p>
          <h3 className="text-2xl sm:text-3xl font-serif text-foreground leading-tight mb-5">{title}</h3>

          <div className="flex gap-2 mb-6">
            {colors.map((c) => (
              <span
                key={c.id}
                className="flex-1 h-12 rounded-xl shadow-inner"
                style={{ backgroundColor: c.hex }}
                title={c.name}
              />
            ))}
          </div>

          <ul className="space-y-1.5">
            {traits.slice(0, 3).map((trait) => (
              <li key={trait} className="text-sm text-foreground flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                {trait}
              </li>
            ))}
          </ul>

          <p className="mt-6 text-[10px] text-muted-foreground">mychromamind.com</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {downloading ? "Preparing…" : "Download Card"}
        </button>
        <button
          type="button"
          onClick={handleCopyInvite}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted transition-colors"
        >
          <Users className="w-4 h-4" />
          {copiedInvite ? "Invite link copied!" : "Invite a Friend"}
        </button>
      </div>

      <ShareButtons
        label="Share your Color Identity"
        text={`I got "${title}" on Mychromamind's color personality quiz. Take it and see our Color Synergy:`}
        url={inviteUrl}
        className="mt-6"
      />
    </div>
  );
}
